import React, {useEffect, useRef, useState} from 'react';
import {useRouteNode, Link} from 'react-router5';
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import { buildChart } from './grid_zoomchart';
import { transformData, updateNode, chartToggleEndPosition } from './grid_transform_data';
import { Panel, PanelEdit } from './grid_panel';
import { SettingsPanel } from './settings_panel';
import SearchCarto from "../carto/searchCarto";
import { upsertGridCE, deleteGridCE, changeOwner } from "../api";

const height = 600;
const x_max = 800;

// name could be grid or explore.theme.briefing
function Grid({ name }) {
  const { t } = useTranslation();
  const { route, router } = useRouteNode(name);
  const app_theme = useSelector((state) => state.theme.value);

  const editing_mode = route.name.startsWith("grid.edit") && route.name != "grid.edit";

  if (! route.data.grid) {
    return <div style={{padding: "10px"}}>{t("No grid associated to the theme")}</div>
  }
  if (route.data.grid.acl.can_user_edit_grid === false && route.name.indexOf("edit") !== -1) {
    return <div style={{padding: "10px"}}>{t("Unauthorized access for edition")}</div>
  }

  return (
    editing_mode
    ? <GridEdit route={route} router={router} app_theme={app_theme} />
    : <GridRO route={route} router={router} edit_panel={route.name == "grid.edit"} app_theme={app_theme} />
  );
}

// check wether we come from grid or theme
// return either the root url or the root w/ the type if any
function buildShowRoute(original_route_name, type) {
  const root = original_route_name.startsWith("grid") ? "grid" : "explore.theme.briefing";
  return type ? root + "." + type : root;
}


function GridRO({ route, router, edit_panel, app_theme }) {
  const {name, data, params: route_params} = route;
  const { t } = useTranslation();
  const ref = useRef();

  const { id: theme_id } = route_params;

  let col2;

  const [chartAPI, setChartAPI] = useState();

  const [fullscreen, setFullscreen] =  useState(false);

  let ce = null;

  const isEditable = () => false;

  const onBack = (e) => {
    const route = buildShowRoute(name)
    router.navigate(route, {id: theme_id}, {reload: true})
  }

  if (route_params.contact_id) {
    const contact = ce = data.grid.contact_nodes.find(x => x.poi_contact_id == route_params.contact_id)

    const onEdit = (e) => {
      router.navigate("grid.edit.contact", {id: theme_id, contact_id: contact.poi_contact_id}, {reload: true})
    }
    col2 = <Panel contact_node={contact} onEdit={onEdit} onBack={onBack} acl={data.grid.acl} />;
  } else if (route_params.entity_id) {
    const entity = ce = data.grid.entity_nodes.find(x => x.poi_entity_id == route_params.entity_id)

    const onEdit = (e) => {
      router.navigate("grid.edit.entity", {id: theme_id, entity_id: entity.poi_entity_id}, {reload: true})
    }

    col2 = <Panel entity_node={entity} onEdit={onEdit} onBack={onBack} acl={data.grid.acl} />;
  }

  const onClick = (e) => {
    if (! e.clickNode) { return; }
    if (! e.clickNode.data.old_id) { return; }

    const node = e.clickNode;
    
    const {old_id: id, type} = e.clickNode.data;

    const route = buildShowRoute(name, type);

    const params = {id: theme_id, [type + "_id"]: id}

    // console.log(route, params);
    // router.navigate(route, params, {reload: true})
    router.navigate(route, params, {reload: false})
  }

  useEffect(() => {
    const {nodes, links} = transformData(data.grid, x_max, isEditable);

    // set chartAPI

    const chartAPI = buildChart({
      element: ref.current,
      height,
      ce_nodes: nodes,
      ce_links: links,
      onNodeClick: onClick,
      updateNode: () => {},
      onFullScreen: (graph) => { setFullscreen((fullscreen) => !fullscreen) }
    });

    if (app_theme === "dark") {
      chartAPI.chart.customize("dark");
    }
    setChartAPI(chartAPI);

  }, [])

  const [open, setOpen] = useState(false);

  // FIXME: test if the current user can edit
  return <div className={"grid-ro grid-main " + (fullscreen ? "grid-container-fullscreen" : "")}>
    <div className="grid-wrapper">
      { data.grid.acl.can_user_edit_grid &&
	  <div className="c-carto_settings">
	    <div className="c-img_buttons">
	      <span onClick={()=>{setOpen(true)}} className="c-button_edit" title={t("Update grid nodes")} />

	      <Link className="c-button_settings"
		title={t("Update grid settings")}
		href="" routeName={`grid.edit`} routeParams={{id: theme_id}}
		routeOptions={{reload: true}}>
	      </Link>
	    </div>
	  </div>
      }
      <div ref={ref} style={{height: "100%"}} />
    </div>

    {col2 && !edit_panel && <div key={name + "_" + (route_params.contact_id || route_params.entity_id)} className="grid-col2">
      { col2 }
    </div>}

    {
      edit_panel &&
      <div className="grid-col2">
	<SettingsPanel data={data} router={router} theme_id={theme_id} />
      </div>
    }

    { open && <div className={`c-container_bg active`}>
	<div className="c-popup_div">
	    <div className="c-popup_header">
		<span className="c-popup_close" onClick={() => {setOpen(false)}} />
		<p>{t("Add an entity or a contact to the grid")}</p>
		<div />
	    </div>
	    <div className="c-popup_content">
	      <SearchCarto
		handleFocus={(id, type) => {
		  router.navigate("grid.edit." + type, {id: theme_id, [type + "_id"]: id}, {reload: true});
		}}
		dataListEntities={data.entities}
		dataListContacts={data.contacts} />
	    </div>
	</div>
      </div>
    }
  </div>
}

function defaultLinkGrid(contact) {
  return {
      chessboard_id: 1,
      label: "",
      role: "",
      information: "",
      objective: "",
      start_coord: [0, 0],
      end_coord: [null, null],
      target: false,
      level: "secondary",
      style: {}
  };
}

function defaultContactGrid(contact) {
  let v = defaultLinkGrid()
  v.poi_contact = contact;
  v.poi_contact_id = contact.id;
  v.label = contact.firstname + " " + contact.lastname;
  return v;
}

function defaultEntityGrid(entity) {
  let v = defaultLinkGrid()
  v.poi_entity = entity;
  v.poi_entity_id = entity.id;
  v.label = entity.name;
  return v;
}

function getCE(id, type, data) {
  if (type == "contact") {
    let existing = data.grid.contact_nodes.find(x => x.poi_contact_id == id);

    if (existing) { return existing; }

    return defaultContactGrid(data.contacts.find(x => x.id == id))
  } else {
    const existing = data.grid.entity_nodes.find(x => x.poi_entity_id == id)

    if (existing) { return existing; }

    return defaultEntityGrid(data.entities.find(x => x.id == id))
  }
}

function GridEdit({ route, router, app_theme }) {
  const {name, data, params: route_params} = route;

  const { t } = useTranslation();
  const ref = useRef();

  const [_route, _edit, type] = route.name.split(".");

  const { id: theme_id, contact_id, entity_id } = route_params;

  // by using (ce_data || ce) we make sure we are up to date
  // refac could be to use the whole node set...
  const node = getCE(contact_id || entity_id, type, data)
  // console.log(contact_id || entity_id, type, node)
  //
  const [ce_data, setCEData] =  useState(node);
  const [coords, setCoords] =  useState({
    start_coord: node.start_coord,
    start_position: node.start_position,
    end_coord: node.end_coord,
    end_position: node.end_position
  });
  const [chartAPI, setChartAPI] = useState();
  const [fullscreen, setFullscreen] =  useState(false);

  const create = !ce_data.id;

  // the contact should not have changed
  const isEditable = (node_struct) => {
    // by comparing contact_id we can be
    return (
      node_struct
      && node_struct.poi_contact_id == ce_data.poi_contact_id
      && node_struct.poi_entity_id == ce_data.poi_entity_id
    );
  };

  const toggleEndPosition = (add_or_delete) => {
    const position_data = chartToggleEndPosition(
      chartAPI.chart, {...ce_data, ...coords}, add_or_delete, x_max
    )
    setCoords((coords) => {
      return {
	// current_node may not exist at first
	...coords,
	...position_data
      }
    });
  }

  const cancelEdition = () => {
    if (create) {
      router.navigate("grid", {id: theme_id}, {reload: true})
    } else if (name == "grid.edit.contact") {
      router.navigate("grid.contact", {id: theme_id, contact_id: ce_data.poi_contact_id}, {reload: true})
    } else if (name == "grid.edit.entity") {
      router.navigate("grid.entity", {id: theme_id, entity_id: ce_data.poi_entity_id}, {reload: true})
    }
  }

  const onDelete = () => {
    deleteGridCE(theme_id, type, ce_data.poi_contact_id || ce_data.poi_entity_id).then(() => {
      // TODO: then
      router.navigate("grid", {id: theme_id}, {reload: true});
    })
  }

  const onSubmitSuccess = () => {
    router.navigate(
      `grid.${type}`,
      {id: theme_id, [type + "_id"]: node.poi_contact_id || node.poi_entity_id},
      {reload: true}
    );
  }

  const col2 = <PanelEdit
    node={ce_data} type={type} coords={coords} theme_id={theme_id}
    toggleEndPosition={toggleEndPosition} cancelEdition={cancelEdition} onDelete={onDelete}
    onSubmitSuccess={onSubmitSuccess}
    />;

  useEffect(() => {
    let grid = data.grid;

    if (create) {
      if (type == "contact") {
	grid = {...grid, contact_nodes: grid.contact_nodes.concat([ce_data])}
      } else {
	grid = {...grid, entity_nodes: grid.entity_nodes.concat([ce_data])}
      }
    }

    const {nodes, links} = transformData(grid, x_max, isEditable);

    // set chartAPI

    const chartAPI = buildChart({
      element: ref.current,
      height,
      ce_nodes: nodes,
      ce_links: links,
      onNodeClick: () => {},
      updateNode: (chart, node, initial_data) => {
	const position_data = updateNode(chart, node, x_max);

	if (position_data) {
	  setCoords((coords) => {
	    return {
	      // current_node may not exist at first
	      ...coords,
	      ...position_data
	    }
	  });
	}
      },
      onFullScreen: (graph) => { setFullscreen((fullscreen) => !fullscreen) }
    });

    if (app_theme === "dark") {
      chartAPI.chart.customize("dark");
    }
    setChartAPI(chartAPI);

  }, [])

  return <div className={"grid-edit grid-main " + (fullscreen ? "grid-container-fullscreen" : "")}>
    <div className="grid-wrapper">
      <div ref={ref} style={{height: "100%"}} />
    </div>

    {col2 && <div key={name + "_" + (route_params.contact_id || route_params.entity_id)} className="grid-col2">
      { col2 }
    </div>}
  </div>

}

export { Grid, GridRO };
